import React, { useState, useEffect } from "react";
import {
	Button,
	Table,
	Modal,
	Input,
	Upload,
	message,
	Space,
	Dropdown,
	Typography,
} from "antd";
import {
	FileOutlined,
	UploadOutlined,
	DeleteOutlined,
	EditOutlined,
	MoreOutlined,
	FolderOpenOutlined,
	DownloadOutlined,
	FilePdfOutlined,
	RollbackOutlined,
	EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Layout from "../layout/Layout";
import { api } from "../utils/Api";

const { Title, Text } = Typography;

const Submitals = ({ history }) => {
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(false);

	// Modals
	const [uploadFileModal, setUploadFileModal] = useState(false);
	const [renameModal, setRenameModal] = useState(false);

	// Form states
	const [fileList, setFileList] = useState([]);
	const [renameItem, setRenameItem] = useState(null);
	const [newName, setNewName] = useState("");

	const [refresh, setRefresh] = useState(false);

	const [previewModal, setPreviewModal] = useState(false);
	const [previewUrl, setPreviewUrl] = useState("");

	useEffect(() => {
		getFiles();
	}, [refresh]);

	// Get files
	const getFiles = async () => {
		setLoading(true);
		try {
			const token = localStorage.getItem("token");
			const { data } = await axios.get(`${api}/submital/get/all`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			setFiles(data.payload);
		} catch (error) {
			message.error("Error loading folders");
			console.error(error);
		}
		setLoading(false);
	};

	// Upload file
	const handleUploadFile = async () => {
		if (fileList.length === 0) {
			message.warning("Please select a file");
			return;
		}

		const formData = new FormData();
		formData.append("file", fileList[0]);

		try {
			const token = localStorage.getItem("token");
			const { data } = await axios.post(`${api}/submital/create`, formData, {
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "multipart/form-data",
				},
			});

			if (data.success) {
				message.success(data.message);
			} else {
				message.error(data.message);
			}

			setUploadFileModal(false);
			setFileList([]);
			setRefresh((prev) => !prev);
		} catch (error) {
			message.error(error.response?.data?.error || "Error uploading file");
			console.error(error);
		}
	};

	// Delete file
	const handleDeleteFile = async (fileId) => {
		try {
			const token = localStorage.getItem("token");
			const { data } = await axios.delete(`${api}/submital/delete/${fileId}`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (data.success) {
				message.success(data.message);
				setRefresh((prev) => !prev);
			} else {
				message.error(data.message);
			}
		} catch (error) {
			message.error(error.response?.data?.error || "Error deleting file");
			console.error(error);
		}
	};

	// Rename folder/file
	const handleRename = async () => {
		if (!newName.trim()) {
			message.warning("Please enter a new name");
			return;
		}

		try {
			const token = localStorage.getItem("token");

			const { data } = await axios.put(
				`${api}/submital/update/${renameItem.id}`,
				{ originalName: newName },
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			if (data.success) {
				message.success(data.message);
			} else {
				message.error(data.message);
			}

			setRenameModal(false);
			setRenameItem(null);
			setNewName("");

			setRefresh((prev) => !prev);
		} catch (error) {
			message.error(error.response?.data?.error || "Error renaming");
			console.error(error);
		}
	};

	// Open modals
	const openRenameModal = (item, type) => {
		setRenameItem({ id: item._id, type });
		setNewName(type === "folder" ? item.name : item.originalName);
		setRenameModal(true);
	};

	const getFileIcon = (extension) => {
		switch (extension) {
			case ".pdf":
				return { icon: <FilePdfOutlined />, color: "#ff4d4f" };
			default:
				return { icon: <FileOutlined />, color: "#8c8c8c" };
		}
	};

	// Table columns
	const columns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text, record) => {
				const fileInfo = getFileIcon(record.extension);
				return (
					<Space>
						{React.cloneElement(fileInfo.icon, {
							style: { fontSize: 20, color: fileInfo.color },
						})}
						<span>{record.originalName || text}</span>
						<EyeOutlined
							style={{ cursor: "pointer", fontSize: 20 }}
							onClick={() => {
								setPreviewUrl(record.fileUrl);
								setPreviewModal(true);
							}}
						/>
					</Space>
				);
			},
		},
		{
			title: "Size",
			dataIndex: "fileSize",
			key: "size",
			width: 120,
			render: (size) => (size ? formatFileSize(size) : "-"),
		},
		{
			title: "Modified",
			dataIndex: "updatedAt",
			key: "modified",
			width: 180,
			render: (date) => new Date(date).toLocaleString(),
		},
		{
			title: "Created By",
			dataIndex: "createdBy",
			key: "createdBy",
			width: 180,
			render: (value) => <Text>{value?.name || ""}</Text>,
		},
		{
			title: "Actions",
			key: "actions",
			width: 100,
			render: (_, record) => {
				const canEditDelete = record.isOwner;

				const items = [
					{
						key: "file-download",
						label: "Download",
						icon: <DownloadOutlined />,
						onClick: () => window.open(record.fileUrl, "_blank"),
					},
					canEditDelete && {
						key: "rename",
						label: "Rename",
						icon: <EditOutlined />,
						onClick: () => openRenameModal(record, record.type),
					},
					canEditDelete && {
						key: "delete",
						label: "Delete",
						icon: <DeleteOutlined />,
						danger: true,
						onClick: () => {
							Modal.confirm({
								title: `Delete file?`,
								content: "This action cannot be undone",
								onOk: () => {
									handleDeleteFile(record._id);
								},
							});
						},
					},
				].filter(Boolean);

				return items.length > 0 ? (
					<Dropdown menu={{ items }} trigger={["click"]}>
						<Button icon={<MoreOutlined />} />
					</Dropdown>
				) : null;
			},
		},
	];

	// Format file size
	const formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
	};

	const tableData = [
		...files.map((file) => ({
			...file,
			key: file._id,
		})),
	];

	const goBack = () => {
		history.goBack();
	};

	return (
		<Layout title="Submital Packages" description="Approved Mix Designs">
			<Title level={2}>
				<FolderOpenOutlined /> File Manager
			</Title>

			{/* Actions */}
			<Space style={{ marginBottom: 16 }}>
				<Button
					type="primary"
					size="large"
					icon={<RollbackOutlined />}
					onClick={goBack}
				/>
				<Button
					icon={<UploadOutlined />}
					type="default"
					onClick={() => setUploadFileModal(true)}
					size="large"
				>
					Upload File
				</Button>
			</Space>

			{/* Table */}
			<Table
				columns={columns}
				dataSource={tableData}
				loading={loading}
				pagination={false}
			/>

			{/* Upload File Modal */}
			<Modal
				title="Upload File"
				open={uploadFileModal}
				onOk={handleUploadFile}
				onCancel={() => {
					setUploadFileModal(false);
					setFileList([]);
				}}
			>
				<Upload
					beforeUpload={(file) => {
						setFileList([file]);
						return false;
					}}
					accept=".pdf"
					fileList={fileList}
					onRemove={() => setFileList([])}
					maxCount={1}
				>
					<Button icon={<UploadOutlined />}>Select File</Button>
				</Upload>
			</Modal>

			{/* Rename Modal */}
			<Modal
				title="Rename"
				open={renameModal}
				onOk={handleRename}
				onCancel={() => {
					setRenameModal(false);
					setRenameItem(null);
					setNewName("");
				}}
			>
				<Input
					placeholder="New name"
					value={newName}
					onChange={(e) => setNewName(e.target.value)}
					onPressEnter={handleRename}
				/>
			</Modal>

			{/* Preview PDF Modal */}
			<Modal
				title="PDF Preview"
				open={previewModal}
				onCancel={() => {
					setPreviewModal(false);
					setPreviewUrl("");
				}}
				footer={null}
				width={800}
			>
				{previewUrl ? (
					<iframe
						src={previewUrl}
						title="PDF Preview"
						width="100%"
						height="600px"
						style={{ border: "none" }}
					/>
				) : (
					<p>No preview available</p>
				)}
			</Modal>
		</Layout>
	);
};

export default Submitals;
